import * as React from 'react'
import { Check, Copy } from 'lucide-react'
import { ASSISTANT_CONNECTION_ADDRESS } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { SectionHeader } from '@/components/ui/page-header'
import { Tabs } from '@/components/ui/tabs'

type Assistant = 'claude' | 'grok' | 'muse' | 'copilot'

const ASSISTANTS: { value: Assistant; label: string }[] = [
  { value: 'claude', label: 'Claude' },
  { value: 'grok', label: 'Grok' },
  { value: 'muse', label: 'Muse' },
  { value: 'copilot', label: 'Copilot' },
]

/** A tab's value as an assistant, or Claude for anything else. */
function assistantOf(value: string): Assistant {
  return ASSISTANTS.find((a) => a.value === value)?.value ?? 'claude'
}

/** A numbered list of steps. */
function Steps({ children }: { children: React.ReactNode }): React.ReactElement {
  return <ol className="list-decimal space-y-2 pl-5 text-sm text-ink">{children}</ol>
}

/** A label in the other app, set apart so it can be found on screen. */
function Term({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="font-medium">{children}</span>
}

/** A note under a set of steps. */
function Note({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-sm text-ink-muted">{children}</p>
}

/** A link out to the assistant, opened beside this page. */
function Out({ href, children }: { href: string; children: React.ReactNode }): React.ReactElement {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
      {children}
    </a>
  )
}

function ClaudeSteps(): React.ReactElement {
  return (
    <div className="space-y-3">
      <Note>Works in Claude Desktop and at claude.ai, on any plan. The Free plan allows one connector like this.</Note>
      <Steps>
        <li>
          In Claude, open <Term>Customize</Term>, then <Term>Connectors</Term>, and choose{' '}
          <Term>Add custom connector</Term>. (<Out href="https://claude.ai/customize/connectors">Open it in Claude</Out>)
        </li>
        <li>Name it FLOH Venues, and paste the connection address into the URL field.</li>
        <li>
          If Claude asks how to sign in, choose <Term>Sign in now</Term> and <Term>Register automatically</Term>.
        </li>
        <li>
          Choose <Term>Add</Term>, then <Term>Connect</Term>. Claude brings you here to sign in and choose what it
          may do.
        </li>
      </Steps>
      <Note>
        On a Claude Team or Enterprise plan, an owner adds it once, under <Term>Organization settings</Term>,{' '}
        <Term>Connectors</Term>, <Term>Add</Term>, <Term>Custom</Term>. Everyone then finds it under{' '}
        <Term>Customize</Term>, <Term>Connectors</Term> and chooses <Term>Connect</Term>.
      </Note>
    </div>
  )
}

function GrokSteps(): React.ReactElement {
  return (
    <div className="space-y-3">
      <Steps>
        <li>
          Go to <Out href="https://grok.com/connectors">grok.com/connectors</Out>.
        </li>
        <li>
          Choose <Term>New Connector</Term>, then <Term>Custom</Term>.
        </li>
        <li>Paste the connection address.</li>
        <li>Grok brings you here to sign in. Sign in and choose what it may do.</li>
      </Steps>
      <Note>On a Grok Business or Enterprise account, an admin may need to allow connectors first.</Note>
    </div>
  )
}

function MuseSteps(): React.ReactElement {
  return (
    <div className="space-y-3">
      <Note>Meta’s Muse, on the web, in its apps or in WhatsApp. Muse is available in the US and Canada.</Note>
      <Steps>
        <li>
          In Muse, open <Term>Settings</Term>, then <Term>Connectors</Term>, and choose{' '}
          <Term>Add custom connector</Term>.
        </li>
        <li>Paste the connection address.</li>
        <li>Muse opens a sign-in page here. Sign in and choose what it may do.</li>
        <li>Ask Muse about one of your events to start.</li>
      </Steps>
    </div>
  )
}

function CopilotSteps(): React.ReactElement {
  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <p className="text-sm font-medium text-ink">Microsoft Copilot, through Copilot Studio</p>
        <Note>
          Microsoft Copilot reaches FLOH Venues through an agent your organization builds in Copilot Studio.
        </Note>
        <Steps>
          <li>
            On the agent’s <Term>Tools</Term> page, choose <Term>Add a tool</Term>, <Term>New tool</Term>, then{' '}
            <Term>Model Context Protocol</Term>.
          </li>
          <li>
            Name it FLOH Venues, describe it as “Venues, events and floor plans in FLOH Venues”, and paste the
            connection address as the <Term>Server URL</Term>.
          </li>
          <li>
            For authentication choose <Term>OAuth 2.0</Term>, then <Term>Dynamic discovery</Term>, and{' '}
            <Term>Create</Term>.
          </li>
          <li>
            Choose <Term>Create a new connection</Term>, sign in here and choose what it may do, then{' '}
            <Term>Add to agent</Term>.
          </li>
        </Steps>
      </div>
      <div className="space-y-3">
        <p className="text-sm font-medium text-ink">GitHub Copilot, in VS Code</p>
        <Steps>
          <li>
            Run <Term>MCP: Add Server</Term> from the Command Palette, and choose <Term>HTTP</Term>.
          </li>
          <li>Paste the connection address, and name it FLOH Venues.</li>
          <li>The first time Copilot uses it, VS Code asks you to sign in. Sign in here and choose what it may do.</li>
        </Steps>
      </div>
    </div>
  )
}

const STEPS: Record<Assistant, () => React.ReactElement> = {
  claude: ClaudeSteps,
  grok: GrokSteps,
  muse: MuseSteps,
  copilot: CopilotSteps,
}

/**
 * How to connect an AI assistant to this account, one assistant at a time.
 *
 * Every assistant here connects the same way underneath: it is given the
 * connection address, sends the person to sign in, and the consent screen lets
 * them choose what it may see and change. What differs is where each app keeps
 * the setting, so that is all a tab says. The steps follow each app's own
 * documentation as of October 2026 and name its buttons as it does.
 *
 * Shown to the roles that can hold a token, the same as API tokens: a
 * connection is one, and it appears in that list once made.
 */
export function AgentsSection(): React.ReactElement {
  const [assistant, setAssistant] = React.useState<Assistant>('claude')
  const [copied, setCopied] = React.useState(false)
  const AssistantSteps = STEPS[assistant]

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(ASSISTANT_CONNECTION_ADDRESS)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard refused (no permission, insecure context): the address is on
      // screen and selectable, so there is still a way through.
      setCopied(false)
    }
  }

  return (
    <section className="space-y-5">
      <div className="space-y-2">
        <SectionHeader title="Agents" />
        <p className="max-w-[62ch] text-sm text-ink-muted">
          Connect an AI assistant, such as Claude, Grok, Muse or Copilot, to your venues and events. It works as you
          and reaches only what you can.
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-ink">Connection address</p>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
            {ASSISTANT_CONNECTION_ADDRESS}
          </code>
          <Button type="button" variant="outline" onClick={() => void copy()}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-xs text-ink-muted">Every assistant asks for this. Paste it where its steps say.</p>
      </div>

      <Tabs items={ASSISTANTS} value={assistant} onValueChange={(value) => setAssistant(assistantOf(value))} />
      <AssistantSteps />

      <p className="text-sm text-ink-muted">
        When you sign in, you choose what the assistant may see and what it may change. Each connection then appears
        under API tokens below, with a list of everything it has changed, and removing it there disconnects it.
        Uploading photographs and inviting people stay in the app.
      </p>
    </section>
  )
}
